import { Color4, Engine, EngineView, GizmoManager, Scene } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs';
import { CanvasEntity } from '@modules/babylonjs/src/systems/CanvasSystem';
import { Resolution } from '@modules/babylonjs/src/QualitySettings';
import { Injectable, With, World, Zed } from '@modules/core';

import { ZEDCamera } from '../camera';
import { onResize } from '../observable';

type Entity = CanvasEntity;

export class RenderManager implements Injectable {
  public static readonly INJECTABLE_TOKEN = Symbol.for('RenderManager');

  /** 싱글턴 패턴 */
  private static singletonInstance: RenderManager | undefined;
  public static get instance(): RenderManager | undefined {
    return this.singletonInstance;
  }
  public static RenderUpdate = (onceUpdate: boolean = true) => {
    if (onceUpdate) {
      RenderManager.instance?.runRenderLoop(true);
    } else {
      RenderManager.instance!.forceRender = true;
    }
  };

  private readonly withCanvas;
  private readonly canvasViews = new Map<EngineView, With<Entity, 'Canvas'>>();
  private readonly canvasNextFrameTime = new Map<EngineView, number>();

  private scene: Scene;
  private engine: Engine;
  private gizmoManager?: GizmoManager | undefined;
  private resizeDelay = -1;

  public playingVideoCount = 0;
  public forceRender: boolean = false;

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
  }) {
    RenderManager.singletonInstance = this;

    this.withCanvas = world.with('Canvas', 'EngineView');
    this.withCanvas.onEntityAdded.subscribe((entity) => {
      this.canvasViews.set(entity.EngineView, entity);
      if (this.canvasViews.size > 1) {
        this.runCanvasViewPauser();
      }
    });
    this.withCanvas.onEntityRemoved.subscribe((entity) => {
      this.canvasViews.delete(entity.EngineView);
    });

    this.engine = babylonjs.engine;
    this.scene = babylonjs.scene;
    this.gizmoManager = undefined; // FIXME: get gizmoManager

    this.engine.onResizeObservable.add(this.onResize);

    this.engine.stopRenderLoop();
    this.initializeScene();
    this.startManualRenderLoop();
  }

  private onResize = () => {
    this.resizeDelay = 5;
    onResize.notifyObservers(undefined);
  };

  private initializeScene() {
    this.scene.clearColor = new Color4(0, 0, 0, 0);
    this.scene.autoClear = false; // Color buffer
    this.scene.autoClearDepthAndStencil = false; // Depth and stencil, obviously
    this.scene.skipFrustumClipping = true;
    this.scene.skipPointerMovePicking = true;
    this.scene.blockMaterialDirtyMechanism = true;
    this.scene.environmentIntensity = 1.8;
  }

  private getCamera = (): ZEDCamera => {
    return this.scene.activeCamera as ZEDCamera;
  };

  dispose = () => {};

  private highPerfomance = true;
  private maxFps = 60;
  private defaultLimitRenderFps = 30;
  private prevforceRender = false;
  private limitRenderFps = 0;
  startManualRenderLoop = () => {
    const scene = this.scene;
    if (!!scene) {
      scene.getEngine().runRenderLoop(() => {
        //--------------------
        // loop Logic
        // ...
        //--------------------
        // fps check

        //--------------------
        // rendering Logic
        //--------------------
        const isGizmo = !!(this.gizmoManager && this.gizmoManager.attachableNodes);
        const isAnimating = scene.animatables.length > 0;
        const isPlayingVideo = this.playingVideoCount > 0;
        // 애니메이션 출력 중이거나 비디오 출력 중 강제 렌더링
        const forceRender = isGizmo || isAnimating || isPlayingVideo || this.forceRender;
        // 카메라 가속이 남아있을 경우 추가적인 강제 렌더링
        const isRemainInertia = this.isRemainInertia();
        // 강제 렌더링 시 해상도 최적화 적용을 끝난 후 1프레임 렌더링 함으로써 원복 시키기 위한 값
        const finishedForceRender = this.prevforceRender && !forceRender;
        let fps =
          forceRender || isRemainInertia || finishedForceRender || this.resizeDelay-- === 0
            ? this.getTargetFPS()
            : this.limitRenderFps;

        if (this.initResolution) {
          this.applyResolution(this.initResolution);
        }
        this.prevforceRender = forceRender;

        if (fps <= 0) return;

        const view = this.engine.activeView;
        const now = performance.now();

        scene.render();

        if (!view) return;

        fps = this.canvasViews.get(view)?.Canvas.targetFps ?? (fps || 60);

        const renderDuration = performance.now() - now;
        const pauseDuration = 1000 / fps - renderDuration;
        this.canvasNextFrameTime.set(view, now + pauseDuration);
      });
    }
  };

  private canvasViewPauserId: number | undefined;
  private runCanvasViewPauser = () => {
    const resetCanvasViewPauser = () => {
      if (this.canvasViewPauserId) {
        cancelAnimationFrame(this.canvasViewPauserId);
        this.canvasViewPauserId = undefined;
      }
      this.canvasViews.forEach((_, view) => {
        view.enabled = true;
      });
    };

    const canvasViewPauser = (time: DOMHighResTimeStamp) => {
      this.canvasNextFrameTime.forEach((nextFrameTime, view) => {
        if (!this.canvasViews.has(view)) {
          this.canvasNextFrameTime.delete(view);
          return;
        }
        view.enabled = time >= nextFrameTime;
      });
    };

    const scheduleCanvasViewPauser = (time = performance.now()) => {
      if (this.canvasViews.size < 2) {
        resetCanvasViewPauser();
        return;
      }
      canvasViewPauser(time);
      this.canvasViewPauserId = requestAnimationFrame(scheduleCanvasViewPauser);
    };

    resetCanvasViewPauser();
    scheduleCanvasViewPauser();
  };

  renderRequestCount = 0;
  renderUpdate = () => {
    this.runRenderLoop(true);
  };
  runRenderLoop = (justOnceUpdate: boolean = false) => {
    ++this.renderRequestCount;
    if (justOnceUpdate) {
      this.limitRenderFps = this.getTargetFPS();
      this.scene.onAfterRenderObservable.addOnce(this.stopRenderLoop);
    } else {
      this.limitRenderFps = this.getTargetFPS();
    }
  };

  stopRenderLoop = () => {
    --this.renderRequestCount;
    if (this.renderRequestCount <= 0) {
      this.renderRequestCount = 0;
      this.limitRenderFps = 0;
    }
  };

  enableHighPerfomance = (enable: boolean) => {
    this.highPerfomance = enable;
  };
  getTargetFPS = () => {
    return this.highPerfomance ? this.maxFps : this.defaultLimitRenderFps;
  };

  public isRemainInertia = (): boolean => {
    const camera = this.getCamera();
    return (
      camera.inertialAlphaOffset !== 0 ||
      camera.inertialBetaOffset !== 0 ||
      camera.inertialPanningX !== 0 ||
      camera.inertialPanningY !== 0 ||
      camera.inertialRadiusOffset !== 0
    );
  };

  private initResolution?: Resolution;
  private currentResolution?: Resolution;
  private forceRenderResolution?: Resolution | undefined;
  private maxWidth?: number;
  private maxHeight?: number;
  private hardwareScalingLevel?: number;
  public getResolution() {
    return this.initResolution;
  }
  public setResolution(resolution: Resolution) {
    if (this.initResolution === resolution) return;
    this.initResolution = resolution;

    const keys = Object.keys(Resolution).flatMap((key) => {
      if (!isNaN(Number(key)) || key == 'None') return [];
      else return key;
    });

    const index = keys.findIndex(
      (key) => key == Resolution[resolution.toString() as keyof typeof Resolution].toString()
    );
    if (index === -1) {
      this.forceRenderResolution = undefined;
    } else {
      this.forceRenderResolution = Resolution[keys[Math.min(index + 1, keys.length - 1)] as keyof typeof Resolution];
    }
    this.applyResolution(resolution);

    this.renderUpdate();
  }
  private applyResolution(resolution: Resolution) {
    if (!this.engine.activeView) {
      console.warn('Canvas not ready');
      return;
    }

    this.hardwareScalingLevel = 1 / (window.devicePixelRatio || 1);

    if (this.currentResolution === resolution) return;
    this.currentResolution = resolution;

    this.maxWidth = this.engine.activeView.target.clientWidth;
    this.maxHeight = this.engine.activeView.target.clientHeight;

    if (resolution == Resolution.MAX) {
      this.engine.setHardwareScalingLevel(this.hardwareScalingLevel);
    } else if (resolution == Resolution.NONE) {
      this.engine.setHardwareScalingLevel(1);
    } else {
      const scale = Math.min(this.maxWidth, this.maxHeight) / resolution;

      // https://zigbang.slack.com/archives/C04EEC82S1E/p1721887538368179?thread_ts=1721887393.792869&cid=C04EEC82S1E
      // this.engine.setHardwareScalingLevel(Math.max(scale * this.hardwareScalingLevel, this.hardwareScalingLevel));
      this.engine.setHardwareScalingLevel(Math.max(scale, this.hardwareScalingLevel));
    }
    // console.log(`Resolution : ${this.engine.getRenderWidth()}x${this.engine.getRenderHeight()}`);
  }
}
